import React, { useState } from "react";
import { toast } from "react-toastify";
import { markAsPaid, InvoiceApiError } from "../../services/invoice.service";
import type { InvoiceResponse } from "../../schemas/invoice.schema";
import { DateInput } from "../../components/ui/DateInput";
import { actionErrorMessage } from "../../utils/network";

type PaymentMethodOption = "cash" | "upi" | "cheque" | "bank_transfer" | "other";

const METHODS: { value: PaymentMethodOption; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "other", label: "Other" },
];

interface Props {
  invoice: InvoiceResponse;
  onClose: () => void;
  onSuccess: (updated: InvoiceResponse) => void;
}

const PaymentModal: React.FC<Props> = ({ invoice, onClose, onSuccess }) => {
  const outstanding = invoice.outstanding_balance_paise ?? invoice.total_paise;
  const [method, setMethod] = useState<PaymentMethodOption>("cash");
  const [reference, setReference] = useState("");
  const [paidOn, setPaidOn] = useState(new Date().toISOString().split("T")[0]);
  const [amount, setAmount] = useState((outstanding / 100).toFixed(2));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const today = new Date().toISOString().split("T")[0];

  const handleConfirm = async () => {
    setInlineError(null);
    const amountPaise = Math.round(parseFloat(amount || "0") * 100);
    if (amountPaise <= 0) {
      setInlineError("Amount must be greater than zero.");
      return;
    }
    if (amountPaise > outstanding) {
      setInlineError(`Amount cannot exceed outstanding balance of Rs ${(outstanding / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const updated = await markAsPaid(invoice.invoice_id, {
        payment_method: method,
        payment_reference: reference.trim() || undefined,
        paid_at: paidOn ? `${paidOn}T00:00:00Z` : undefined,
        amount_collected_paise: amountPaise || undefined,
      });
      const label = updated.status === "partially_paid" ? "partially paid" : "paid";
      toast.success(`Invoice marked as ${label}. Receipt sent to patient.`);
      onSuccess(updated);
    } catch (err) {
      if (err instanceof InvoiceApiError) {
        if (err.isConflict) {
          setInlineError("Invoice is already marked as paid. Please refresh.");
        } else if (err.statusCode === 400 || err.statusCode === 422) {
          setInlineError("Payment date cannot be in the future.");
        } else if (err.statusCode === 403) {
          // Relay the server's reason — it names the missing capability
          // ("Your role does not include the 'Create Bills' permission.").
          setInlineError(
            actionErrorMessage(err, "You do not have permission to mark this invoice."),
          );
        } else if (err.statusCode >= 500) {
          setInlineError("Server error. Please try again.");
        } else {
          setInlineError(err.message);
        }
      } else {
        setInlineError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[400px] max-w-[95vw] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-2">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Record Payment</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {invoice.invoice_number} - {invoice.total_display}
              {invoice.status === "partially_paid" && outstanding < invoice.total_paise && (
                <span className="ml-1 text-amber-600 font-medium">
                  (Outstanding: Rs {(outstanding / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })})
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition mt-1"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="px-6 pb-6 pt-3 flex flex-col gap-4">
          {/* Payment method */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Payment method</label>
            <div className="relative">
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value as PaymentMethodOption)}
                className="w-full px-3 py-2.5 text-base sm:text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none cursor-pointer"
              >
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Reference <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="UPI Txn ID, Cheque number, bank ref, etc."
              className="w-full px-3 py-2.5 text-base sm:text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
          </div>

          {/* Paid on */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Paid on</label>
            <DateInput
              value={paidOn}
              onChange={(e) => {
                const picked = e.target.value;
                if (picked && picked > today) return;
                setPaidOn(picked);
              }}
              max={today}
              className="w-full px-3 py-2.5 text-base sm:text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
          </div>

          {/* Amount received */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Amount received (INR)
              <span className="text-slate-400 font-normal ml-1">max Rs {(outstanding / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">Rs</span>
              <input
                type="number"
                step="0.01"
                min="1"
                max={(outstanding / 100).toFixed(2)}
                value={amount}
                onChange={(e) => {
                  const val = e.target.value;
                  const num = parseFloat(val || "0");
                  const maxAmount = outstanding / 100;
                  if (num > maxAmount) {
                    setAmount(maxAmount.toFixed(2));
                  } else {
                    setAmount(val);
                  }
                }}
                className="w-full pl-9 pr-3 py-2.5 text-base sm:text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
            </div>
          </div>

          {/* Inline error */}
          {inlineError && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <svg className="shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <p className="text-xs">{inlineError}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Processing..." : "Mark as Paid"}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center gap-2 text-xs text-slate-400">
          <svg className="shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          Payment information is handled securely.
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
